import { Check, User } from "lucide-react";
import { BUSINESS, IMG, ASSETS } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { TrustBar } from "@/components/TrustBar";
import { CtaBanner } from "@/components/CtaBanner";

export default function About() {
  const points = [
    `${BUSINESS.yearsInBusiness} years in business`,
    `${BUSINESS.combinedExperience} years combined experience`,
    "Electrical, air conditioning & heat pump expertise",
    "Residential and commercial service",
    "Customer-focused approach",
    "Reliable installation and workmanship",
    "Local Victorian and NSW service areas",
  ];

  return (
    <>
      <PageHero
        label="About Us"
        title="Experienced Trades. Reliable Service. Quality Workmanship."
        subtitle={`Access Electrical and Air Conditioning is a genuine local service company, delivering electrical, heating, cooling and hot water solutions across Victoria and New South Wales.`}
        image={IMG.tech}
        crumbs={[{ label: "About Us" }]}
      />

      <TrustBar />

      {/* Story */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>Our Story</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Trusted electrical, heating & cooling professionals
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              For {BUSINESS.yearsInBusiness} years we've helped homes and businesses stay comfortable and safe. With {BUSINESS.combinedExperience} years of combined industry experience, our team brings genuine expertise across air conditioning, ducted systems, heat pump hot water and electrical work.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              We're focused on quality workmanship, reliable service, and practical, energy-efficient solutions — treating every property with the same care we'd expect in our own home.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-electric/10">
                    <Check className="h-3.5 w-3.5 text-brand-electric" />
                  </span>
                  <span className="text-[15px]">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid gap-4">
              <div className="overflow-hidden rounded-3xl border border-slate-200">
                <img src={IMG.mitsubishiOutdoor} alt="Access team installation project" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="rounded-3xl border border-slate-200 bg-brand-blue p-8 text-white">
                <h3 className="font-display text-lg font-semibold">Our credentials</h3>
                <ul className="mt-4 space-y-3">
                  {BUSINESS.credentials.map((c) => (
                    <li key={c} className="flex items-center gap-3 text-slate-300">
                      <Check className="h-4 w-4 text-brand-gold" /> {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Meet Danrell */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 rounded-3xl border border-slate-200 bg-white p-8 md:grid-cols-[0.9fr_1.1fr] md:p-14 lg:gap-16">
            <Reveal>
              <div className="flex h-full flex-col justify-center rounded-2xl bg-brand-blue p-10 text-white">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10">
                  <User className="h-8 w-8 text-brand-gold" strokeWidth={1.5} />
                </span>
                <h3 className="mt-6 font-display text-3xl font-bold">{BUSINESS.owner}</h3>
                <p className="mt-1 text-brand-gold">Owner — Access Electrical and Air Conditioning</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-col justify-center">
                <SectionLabel>Meet the Owner</SectionLabel>
                <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
                  Led by {BUSINESS.owner}
                </h2>
                <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                  {BUSINESS.owner} leads Access Electrical and Air Conditioning with a hands-on commitment to quality service and professionalism. With practical, real-world expertise across electrical, heating and cooling, {BUSINESS.owner} is focused on delivering reliable results and energy-efficient upgrade solutions for every customer.
                </p>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                  The approach is simple: honest advice, dependable workmanship, and genuine customer support from the first call to the final handover.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
